// What a membership actually permits. This is the module every booking,
// check-in and door-unlock decision routes through, so it stays pure and
// returns a REASON on refusal — the front desk has to be able to explain "no".

import { addDays } from './recurrence.js';

export const PLAN_KINDS = {
  RECURRING_UNLIMITED: 'recurring_unlimited',
  RECURRING_CAPPED: 'recurring_capped',
  PACK: 'pack',
  DROP_IN: 'drop_in',
};

export const DENY = {
  NO_MEMBERSHIP: 'no_membership',
  NOT_STARTED: 'not_started',
  EXPIRED: 'expired',
  CANCELLED: 'cancelled',
  FROZEN: 'frozen',
  DELINQUENT: 'delinquent',
  PROGRAM_NOT_COVERED: 'program_not_covered',
  LIMIT_REACHED: 'limit_reached',
  NO_CREDITS: 'no_credits',
  CREDITS_EXPIRED: 'credits_expired',
};

export const DENY_MESSAGE = {
  [DENY.NO_MEMBERSHIP]: 'No active membership on file.',
  [DENY.NOT_STARTED]: 'Membership has not started yet.',
  [DENY.EXPIRED]: 'Membership has expired.',
  [DENY.CANCELLED]: 'Membership was cancelled.',
  [DENY.FROZEN]: 'Membership is on hold.',
  [DENY.DELINQUENT]: 'Account has an overdue balance.',
  [DENY.PROGRAM_NOT_COVERED]: 'This plan does not include that program.',
  [DENY.LIMIT_REACHED]: 'Usage limit reached for this period.',
  [DENY.NO_CREDITS]: 'No credits remaining.',
  // Deliberately separate from NO_CREDITS. Both are "no", but one is solved by
  // selling a pack and the other by honouring one already paid for, and the
  // front desk cannot tell them apart without being told.
  [DENY.CREDITS_EXPIRED]: 'These credits have expired.',
};

const allow = () => ({ ok: true });
const deny = (code, detail) => ({ ok: false, code, reason: DENY_MESSAGE[code], ...detail });

/** Is a date inside any of the membership's freeze windows? */
export function isFrozenOn(membership, dateKey) {
  return (membership.freezes || []).some(
    (f) => dateKey >= f.from && (!f.to || dateKey <= f.to),
  );
}

/**
 * The window of dates a capped plan counts usage over.
 * Returns [fromKey, toKey] inclusive.
 */
export function usageWindow(plan, membership, dateKey) {
  const period = plan.limitPeriod || 'week';
  if (period === 'day') return [dateKey, dateKey];
  if (period === 'week') {
    // Weeks run Monday-Sunday; ISO convention, and it matches how gyms talk.
    const dow = (new Date(`${dateKey}T00:00:00Z`).getUTCDay() + 6) % 7;
    return [addDays(dateKey, -dow), addDays(dateKey, 6 - dow)];
  }
  if (period === 'month') {
    const [y, m] = dateKey.split('-').map(Number);
    const last = new Date(Date.UTC(y, m, 0)).getUTCDate();
    return [`${dateKey.slice(0, 7)}-01`, `${dateKey.slice(0, 7)}-${String(last).padStart(2, '0')}`];
  }
  if (period === 'cycle') {
    return [membership.currentPeriodStart, membership.currentPeriodEnd];
  }
  throw new Error(`usageWindow: unknown period "${period}"`);
}

export function usageInWindow(usageDates, [fromKey, toKey]) {
  return usageDates.filter((d) => d >= fromKey && d <= toKey).length;
}

/**
 * Can this membership be used for this occurrence on this date?
 *
 * membership: {
 *   status: 'active'|'cancelled'|'expired'|'pending',
 *   startDate, endDate?, currentPeriodStart, currentPeriodEnd,
 *   freezes?: [{from, to?}], creditsRemaining?, usageDates?: ['YYYY-MM-DD'],
 *   pastDueCents?: number
 * }
 * plan: { kind, limit?, limitPeriod?, programs?: ['crossfit'], blockWhenPastDue? }
 */
export function checkEntitlement({ membership, plan, occurrence, dateKey }) {
  if (!membership || !plan) return deny(DENY.NO_MEMBERSHIP);

  if (membership.status === 'cancelled') return deny(DENY.CANCELLED);
  if (dateKey < membership.startDate) return deny(DENY.NOT_STARTED);
  if (membership.endDate && dateKey > membership.endDate) return deny(DENY.EXPIRED);
  if (membership.status === 'expired') return deny(DENY.EXPIRED);
  if (isFrozenOn(membership, dateKey)) return deny(DENY.FROZEN);

  // Delinquency blocks access only when the plan says so. Boutique studios
  // usually let a member in and chase the card; 24/7 gyms lock the door.
  if (plan.blockWhenPastDue && (membership.pastDueCents || 0) > 0) {
    return deny(DENY.DELINQUENT, { pastDueCents: membership.pastDueCents });
  }

  // A plan with no `programs` list covers everything.
  const programs = plan.programs;
  const program = occurrence?.program;
  if (programs && programs.length && program && !programs.includes(program)) {
    return deny(DENY.PROGRAM_NOT_COVERED, { program });
  }

  if (plan.kind === PLAN_KINDS.PACK || plan.kind === PLAN_KINDS.DROP_IN) {
    // Credit expiry is a term of sale — "10 classes, use them within 120 days"
    // — so it has to bind before the balance is even consulted. Inclusive of
    // the final day: a pack sold with 30 days is usable on day 30.
    const expiresOn = membership.creditsExpireOn;
    if (expiresOn && dateKey > expiresOn) {
      return deny(DENY.CREDITS_EXPIRED, {
        expiredOn: expiresOn,
        reason: `These credits expired on ${expiresOn}.`,
      });
    }
    const credits = membership.creditsRemaining ?? 0;
    if (credits <= 0) return deny(DENY.NO_CREDITS);
    return { ...allow(), consumes: 'credit', creditsAfter: credits - 1 };
  }

  if (plan.kind === PLAN_KINDS.RECURRING_CAPPED) {
    const window = usageWindow(plan, membership, dateKey);
    const used = usageInWindow(membership.usageDates || [], window);
    if (used >= plan.limit) {
      return deny(DENY.LIMIT_REACHED, { used, limit: plan.limit, window });
    }
    return { ...allow(), consumes: 'usage', used: used + 1, limit: plan.limit, window };
  }

  return { ...allow(), consumes: 'none' };
}

/**
 * Pick the membership a member should book with when they hold several.
 * Prefers burning expiring credits before unlimited plans, so packs don't rot.
 */
export function selectMembership(memberships, plansById, context) {
  const usable = memberships
    .map((m) => ({ m, plan: plansById[m.planId] }))
    .filter(({ m, plan }) => plan && checkEntitlement({ ...context, membership: m, plan }).ok);

  if (!usable.length) return null;

  const rank = ({ plan }) => {
    if (plan.kind === PLAN_KINDS.DROP_IN) return 0;
    if (plan.kind === PLAN_KINDS.PACK) return 1;
    if (plan.kind === PLAN_KINDS.RECURRING_CAPPED) return 2;
    return 3;
  };
  // Within a rank, spend what dies first. `expiresAt` used to be read here and
  // exists on nothing in the codebase, so every comparison was '9999' vs '9999'
  // and the tiebreak silently degraded to array order — exactly the rot this
  // function was written to prevent.
  const diesOn = ({ m }) => m.creditsExpireOn || m.endDate || '9999-12-31';
  usable.sort((a, b) => rank(a) - rank(b) || diesOn(a).localeCompare(diesOn(b)));
  return usable[0].m;
}
